<template>
  <div class="premium-prontuario-editor">
    <!-- Card Principal -->
    <v-card
      class="premium-card"
      flat
    >
      <!-- Top Toolbar -->
      <v-card-text class="premium-toolbar py-3 px-4">
        <v-row
          no-gutters
          align="center"
          class="flex-nowrap toolbar-row"
        >
          <!-- Botão Copiar Último Prontuário -->
          <v-tooltip location="top">
            <template #activator="{ props: tooltip }">
              <v-btn 
               
                :loading="loadProntuario" 
                variant="outlined" 
                class="action-btn copy-btn mr-3"
                size="small"
                v-bind="tooltip"
                @click="copiarUltimoProntuario"
              >
                <template #prepend>
                  <v-icon
                    size="small"
                    class="btn-icon"
                  >
                    content_copy
                  </v-icon>
                </template>
                <span class="btn-text">Copiar último prontuário</span>
              </v-btn>
            </template>
            <span v-if="isUltimoProntuario">Já é o último prontuário</span>
            <span v-else-if="!paciente?.idOrdemServico">Ordem de serviço não disponível</span>
            <span v-else>Copiar prontuário do atendimento anterior</span>
          </v-tooltip>

          <v-spacer />

          <!-- Seção de Modelos -->
          <div class="models-section d-flex align-center">
            <!-- Autocomplete de Modelos -->
            <v-autocomplete 
              v-model="modelo" 
              v-model:search="searchModelo"
              :items="modeloProntuario.modelos" 
              density="compact" 
              variant="outlined" 
              hide-no-data
              color="grey-darken-3"
              item-title="nome" 
              item-value="_id"
              label="Buscar Modelos" 
              placeholder="Digite para buscar" 
              prepend-inner-icon="search" 
              return-object
              hide-details
              class="model-autocomplete mr-2"
              :style="{ minWidth: '200px' }"
            />

            <!-- Botões de Ação dos Modelos -->
            <div class="model-actions d-flex align-center gap-2">
              <v-menu location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    variant="text"
                    icon
                    class="action-icon model-action-btn"
                  >
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item
                    :disabled="!idMedelo"
                    @click="confirmarLimparModelo"
                  >
                    <template #prepend>
                      <v-icon>cleaning_services</v-icon>
                    </template>
                    <v-list-item-title>Limpar modelo</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="dialogNovoModelo = true"
                  >
                    <template #prepend>
                      <v-icon>note_add</v-icon>
                    </template>
                    <v-list-item-title>Novo modelo</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :disabled="!idMedelo || !content || content.length < 8"
                    @click="dialogEditarModelo = true"
                  >
                    <template #prepend>
                      <v-icon>edit_document</v-icon>
                    </template>
                    <v-list-item-title>Editar modelo</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </v-row>
      </v-card-text>

      <v-divider class="border-grey-lighten-2" />

      <!-- Área do Editor -->
      <v-card-text
        class="editor-container pa-0"
        style="height: 500px;"
      >
        <div class="editor-wrapper pa-3">
          <tiptap-editor
            v-show="!isImprime"
            ref="editorRef"
            v-model="content"
            :placeholder="'Descreva o prontuário do paciente...'"
            :min-height="'450px'"
            @update="onEditorUpdate"
          />
        </div>
      </v-card-text>

      <!-- Footer com Status de Salvamento -->
      <v-card-actions class="editor-footer pa-2">
        <v-spacer />
        <div
          v-if="loadSocket"
          class="editor-status d-flex align-center"
        >
          <v-progress-circular
            indeterminate
            size="16"
            width="2"
            class="mr-2"
          />
          <span class="status-text text-caption">Salvando...</span>
        </div>
        <div
          v-else-if="socket && socket.isConnected"
          class="editor-status d-flex align-center"
        >
          <v-icon
            size="16"
            color="success"
            class="mr-1"
          >
            check_circle
          </v-icon>
          <span class="status-text text-caption">Salvo</span>
        </div>
      </v-card-actions>
    </v-card>

    <v-dialog
      v-model="dialogAplicarModelo"
      width="420"
    >
      <v-card variant="outlined" style="background-color: rgb(var(--v-theme-surface)); color: rgb(var(--v-theme-on-surface));">
        <v-card-title>Aplicar modelo</v-card-title>
        <v-card-text>
          Escolha como deseja inserir o modelo
          <strong>{{ modeloPendente?.nome || '' }}</strong>.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelarAplicacaoModelo">Cancelar</v-btn>
          <v-btn variant="outlined" @click="aplicarModeloSelecionado('substituir')">Substituir</v-btn>
          <v-btn color="primary" variant="flat" @click="aplicarModeloSelecionado('acrescentar')">Acrescentar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para Novo Modelo -->
    <v-dialog
      v-model="dialogNovoModelo"
      width="700"
    >
      <v-card
        class="premium-dialog-card"
        style="background-color: rgb(var(--v-theme-surface));"
        variant="outlined"
      >
        <v-card-title class="premium-dialog-header py-3 d-flex align-center text-grey-darken-3">
          <v-icon
            color="grey-darken-3"
            class="mr-2"
          >
            add_circle
          </v-icon>
          <span class="dialog-title text-subtitle-1 font-weight-medium">Novo Modelo de Prontuário</span>
        </v-card-title>

        <v-divider class="border-grey-lighten-2" />

        <v-card-text class="dialog-content pa-4">
          <v-text-field 
            v-model="tituloModelo" 
            label="Título do modelo" 
            variant="outlined"
            prepend-inner-icon="title"
            color="grey-darken-3"
            class="mb-3"
            density="compact"
          />
          
          <div class="dialog-editor-container">
            <div class="editor-label mb-2">
              <span class="label-text text-caption text-grey-darken-2">Conteúdo do modelo:</span>
            </div>
            <tiptap-editor
              ref="dialogEditorRef"
              v-model="dialogContent"
              :placeholder="'Digite o conteúdo do modelo de prontuário...'"
              :min-height="'250px'"
              @update="onDialogEditorUpdate"
            />
          </div>
        </v-card-text>

        <v-divider class="border-grey-lighten-2" />

        <v-card-actions class="dialog-actions pa-3">
          <v-spacer />
          <v-btn 
            color="grey-darken-2" 
            variant="outlined" 
            class="action-btn cancel-btn" 
            size="small"
            @click="dialogNovoModelo = false"
          >
            <v-icon left>
              close
            </v-icon>
            Cancelar
          </v-btn>
          <v-btn 
            color="grey-darken-3"
            :loading="loadModelos" 
            class="action-btn save-model-btn"
            variant="flat"
            size="small"
            @click="cadastrarModeloProntuarioAtendimento"
          >
            <v-icon left>
              check
            </v-icon>
            Criar Modelo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para Editar Modelo -->
    <v-dialog
      v-model="dialogEditarModelo"
      width="700"
    >
      <v-card
        class="premium-dialog-card"
        style="background-color: rgb(var(--v-theme-surface));"
        variant="outlined"
      >
        <v-card-title class="premium-dialog-header py-3 d-flex align-center text-grey-darken-3">
          <v-icon
            color="grey-darken-3"
            class="mr-2"
          >
            edit
          </v-icon>
          <span class="dialog-title text-subtitle-1 font-weight-medium">Editar Modelo</span>
        </v-card-title>

        <v-divider class="border-grey-lighten-2" />

        <v-card-text class="dialog-content pa-4">
          <v-text-field 
            v-model="tituloModelo" 
            label="Título do modelo" 
            variant="outlined"
            prepend-inner-icon="title"
            color="grey-darken-3"
            class="mb-3"
            density="compact"
          />
          
          <div class="dialog-editor-container">
            <div class="editor-label mb-2">
              <span class="label-text text-caption text-grey-darken-2">Conteúdo do modelo:</span>
            </div>
            <tiptap-editor
              ref="dialogEditorRef"
              v-model="dialogContent"
              :placeholder="'Digite o conteúdo do modelo de prontuário...'"
              :min-height="'250px'"
              @update="onDialogEditorUpdate"
            />
          </div>
        </v-card-text>

        <v-divider class="border-grey-lighten-2" />

        <v-card-actions class="dialog-actions pa-3">
          <v-spacer />
          <v-btn 
            color="grey-darken-2" 
            variant="outlined" 
            class="action-btn cancel-btn" 
            size="small"
            @click="dialogEditarModelo = false"
          >
            <v-icon left>
              close
            </v-icon>
            Cancelar
          </v-btn>
          <v-btn 
            color="grey-darken-3"
            class="action-btn save-edit-btn" 
            variant="flat"
            size="small"
            @click="salvarEdicaoModelo"
          >
            <v-icon left>
              check
            </v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Hint para Novo Modelo -->
    <v-dialog
      v-model="hintBtnNovoModelo"
      max-width="450px"
    >
      <v-card
        class="premium-hint-card"
        style="background-color: rgb(var(--v-theme-surface));"
        variant="outlined"
      >
        <v-card-title class="hint-header pa-3 d-flex align-center text-grey-darken-3">
          <v-icon
            color="grey-darken-3"
            class="mr-2"
          >
            info
          </v-icon>
          <span class="hint-title text-subtitle-2 font-weight-medium">Criar Modelo Personalizado</span>
          <v-spacer />
          <v-btn
            icon
            variant="text"
            color="grey-darken-2"
            size="small"
            @click="hintBtnNovoModelo = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider class="border-grey-lighten-2" />
        <v-card-text class="hint-content pa-3">
          <p class="text-body-2 text-grey-darken-2 mb-2">
            Crie um <strong>modelo de prontuário personalizado</strong> para usar em atendimentos futuros.
          </p>
          <div class="info-box pa-2 rounded bg-grey-lighten-4">
            <v-icon
              color="grey-darken-2"
              size="small"
              class="mr-1"
            >
              lightbulb
            </v-icon>
            <span class="text-caption text-grey-darken-2">
              Padronize e agilize seus lançamentos de prontuário.
            </span>
          </div>
        </v-card-text>
        <v-divider class="border-grey-lighten-2" />
        <v-card-actions class="hint-actions pa-3">
          <v-spacer />
          <v-btn
            color="grey-darken-3"
            variant="flat"
            size="small"
            @click="abrirDialogNovoModelo"
          >
            <v-icon left>
              check
            </v-icon>
            Criar Modelo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para feedback -->
    <v-snackbar
      v-model="snackbar.visible"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">
          {{ snackbar.color === 'success' ? 'check_circle' : 
            snackbar.color === 'error' ? 'error' : 
            'info' }}
        </v-icon>
        {{ snackbar.message }}
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import medicoService from '@/services/medico.service'
import { socketServiceInstance } from '@/services/socketService'
import TiptapEditor from './tiptapEditor.vue'
import { base } from '@/services/config'

export default {
  name: 'PremiumProntuarioEditor',
  
  components: {
    TiptapEditor
  },
  
  props: {
    paciente: {
      type: Object,
      default: () => ({})
    },
    idExamesOS: {
      type: Number,
      default: null
    },
    idProntuario: {
      type: Number,
      default: null
    },
    idLaudo: {
      type: Number,
      default: null
    },
    isUltimoProntuario: {
      type: Boolean,
      default: false
    },
    msgProntuario: {
      type: String,
      default: ''
    },
    conteudo: {
      type: String, 
      default: '' 
    },
    listaProntuarios: {
      type: Array,
      default: () => []
    }
  },
  
  emits: ['conteudo', 'prontuarioSalvo', 'iniciou-atendimento', 'salvamento-pendente'],
  
  data() {
    return {
      isImprime: false,
      user: {},
      content: '',
      dialogContent: '',
      tituloModelo: '',
      modelos: [],
      dialogNovoModelo: false,
      dialogEditarModelo: false,
      hintLimparModelo: false,
      hintBtnNovoModelo: false,
      idMedelo: null,
      loadModelos: false,
      searchModelo: '',
      modeloProntuario: { modelos: [] },
      loadProntuario: false,
      modelo: null,
      modeloAtivo: null,
      modeloPendente: null,
      loadIa: false,
      loadSocket: false,
      socket: null,
      prontuarioIdLocal: null,
      debounceTimeout: null,
      snackbar: {
        visible: false,
        message: '',
        color: 'success'
      },
      editorRef: null,
      dialogEditorRef: null,
      prontuarioCarregado: false,
      editorFocused: false,
      hasUnsavedChanges: false,
      pendingAutoSaveResolvers: [],
      dialogAplicarModelo: false
    }
  },
  
  watch: {
    content(newContent) {
      this.$emit('conteudo', newContent)

      if (newContent.length > 10) {
        this.hasUnsavedChanges = true
        this.loadSocket = true
        this.debounce(() => {
          this.emitirAutoSave(newContent)
        }, 2000)()
      }
    },
    
    modelo(newModelo) {
      if (newModelo) {
        this.modeloPendente = newModelo
        this.aplicarModeloSelecionado('acrescentar')
        this.modelo = null
        this.searchModelo = ''
      }
    },
    
    conteudo(newValue) {
      if (newValue !== this.content) {
        this.content = newValue || ''
      }
    },

    idProntuario(newValue) {
      this.prontuarioIdLocal = newValue || null
    },

    idExamesOS(newValue, oldValue) {
      if (newValue && newValue !== oldValue && !this.prontuarioCarregado && !this.conteudo) {
        this.buscarProntuarioPorIdExamesOS()
      }
    },
    
    dialogNovoModelo(newVal) {
      if (newVal) {
        this.dialogContent = this.content
        this.tituloModelo = ''
        this.modelo = null
      }
    },
    
    dialogEditarModelo(newVal) {
      if (newVal) {
        this.dialogContent = this.content
      }
    },
    
    paciente: {
      handler(newPaciente, oldPaciente) {
        const mudouAtendimento =
          this.getIdExamesOS(newPaciente) !== this.getIdExamesOS(oldPaciente) ||
          (newPaciente?.idAtendimento || null) !== (oldPaciente?.idAtendimento || null)

        if (mudouAtendimento) {
          this.resetarEstadoProntuario()
        }

        if (newPaciente && this.getIdExamesOS(newPaciente) && !this.prontuarioCarregado && !this.conteudo) {
          this.buscarProntuarioPorIdExamesOS()
        }
      },
      deep: true,
      immediate: true
    },
    loadSocket(newValue) {
      this.$emit('salvamento-pendente', newValue)
    }
  },
  
  async mounted() {
    this.user = this.$store.getters.usuario
    this.prontuarioIdLocal = this.idProntuario || null
    this.inicializarSocket()
    this.buscarModeloProntuarioAtendimento()
    
    await this.$nextTick()
    
    if (this.conteudo) {
      this.content = this.conteudo
    } else if (this.paciente && this.getIdExamesOS()) {
      await this.buscarProntuarioPorIdExamesOS()
    }
  },
  
  beforeUnmount() {
    if (this.socket) {
      this.socket.disconnect()
    }
  },
  
  methods: {
    async buscarProntuarioPorIdExamesOS() {
      if (this.prontuarioCarregado) return
      
      this.loadProntuario = true
      const idExamesOS = this.getIdExamesOS()
      
      try {
        if (idExamesOS) {
          console.log('Buscando prontuário para idExamesOS:', idExamesOS, this.user)
          const { status, data } = await medicoService.medico.buscarProntuarioPorIdExamesOS(idExamesOS, this.user.id)
          console.log('Resposta do servidor:', status, data);
          
          if (status === 200 && data && data.registro) {
            this.content = data.registro
            this.prontuarioIdLocal = data.idProntuario
            this.prontuarioCarregado = true
            return
          }
        }

        if (this.idProntuario || this.prontuarioIdLocal) {
          await this.buscarProntuarioPorId(this.idProntuario || this.prontuarioIdLocal)
        }
      } catch (error) {
        console.error('Erro ao buscar prontuário:', error)
        this.showSnackbar('Erro ao carregar prontuário', 'error')
      } finally {
        this.loadProntuario = false
      }
    },

    async buscarProntuarioPorId(idProntuario) {
      if (!idProntuario) return

      try {
        const { status, data } = await medicoService.medico.retornarProntuarioPorId(idProntuario)
        console.log('Resposta do servidor por idProntuario:', status, data)

        const registro =
          data?.registro ||
          data?.descricao ||
          data?.prontuario?.descricao ||
          data?.data?.registro ||
          data?.data?.descricao ||
          ''

        if (status === 200 && registro) {
          this.content = registro
          this.prontuarioIdLocal = idProntuario
          this.prontuarioCarregado = true
        }
      } catch (error) {
        console.error('Erro ao buscar prontuário por id:', error)
      }
    },
    
    getIdExamesOS(paciente = this.paciente) {
      return this.idExamesOS || paciente?.idExamesos || paciente?.idExamesOS || null
    },

    resetarEstadoProntuario() {
      this.content = this.conteudo || ''
      this.prontuarioIdLocal = this.idProntuario || null
      this.prontuarioCarregado = Boolean(this.content)
      this.hasUnsavedChanges = false
      this.loadSocket = false
      clearTimeout(this.debounceTimeout)
    },

    emitirAutoSave(conteudo = this.content) {
      if (!this.socket || !this.socket.isConnected) {
        return false
      }

      this.socket.emit('escutarEventoTexto', {
        idProntuario: this.prontuarioIdLocal,
        idEmpresa: this.user.idEmpresa,
        idUnidade: this.user.idUnidade,
        descricao: conteudo,
        idPaciente: this.paciente.idPaciente,
        idUsuario: this.user.id,
        idExamesOS: this.getIdExamesOS(),
        tipo: 'PRONTUARIO'
      })

      return true
    },

    async flushPendingSave() {
      if (!this.hasUnsavedChanges || !this.content?.trim()) {
        return true
      }

      clearTimeout(this.debounceTimeout)
      this.loadSocket = true

      if (this.emitirAutoSave()) {
        const salvoViaSocket = await new Promise(resolve => {
          const resolver = (status) => resolve(status)
          this.pendingAutoSaveResolvers.push(resolver)

          setTimeout(() => {
            const index = this.pendingAutoSaveResolvers.indexOf(resolver)
            if (index >= 0) {
              this.pendingAutoSaveResolvers.splice(index, 1)
              resolve(false)
            }
          }, 4000)
        })

        if (salvoViaSocket) {
          return true
        }
      }

      if (this.prontuarioIdLocal) {
        await this.salvarProntuario()
        return true
      }

      this.showSnackbar('Não foi possível confirmar o salvamento automático antes da navegação', 'warning')
      return false
    },
    
    confirmarLimparModelo() {
      this.modelo = null
      this.modeloAtivo = null
      this.tituloModelo = ''
      this.idMedelo = null
      this.hintLimparModelo = false
      this.showSnackbar('Modelo removido com sucesso', 'success')
    },

    aplicarModeloSelecionado(modo) {
      if (!this.modeloPendente) return

      const modelo = this.modeloPendente
      this.modeloAtivo = modelo
      this.tituloModelo = modelo.nome
      this.idMedelo = modelo._id || null
      this.content = modo === 'substituir'
        ? modelo.descricao
        : (this.content ? `${this.content}<p></p>${modelo.descricao}` : modelo.descricao)
      this.showSnackbar(`Modelo "${modelo.nome}" carregado`, 'info')
      this.searchModelo = ''
      this.cancelarAplicacaoModelo()
    },

    cancelarAplicacaoModelo() {
      this.dialogAplicarModelo = false
      this.modeloPendente = null
      this.modelo = null
      this.searchModelo = ''
    },
    
    abrirDialogNovoModelo() {
      this.hintBtnNovoModelo = false
      this.tituloModelo = ''
      this.modelo = null
      this.dialogContent = this.content
      this.dialogNovoModelo = true
    },
    
    onEditorUpdate(content) {
      this.content = content
      this.$emit('conteudo', content)
    },
    
    onDialogEditorUpdate() {
      // Mantém o dialogContent atualizado
    },
    
    async buscarModeloProntuarioAtendimento() {
      try {
        const response = await medicoService.medico.buscarModeloProntuarioAtendimento(this.user.id)
        if (response.status === 200) {
          this.modeloProntuario = response.data
        }
      } catch (error) {
        console.error('Erro ao buscar modelos:', error)
        this.showSnackbar('Erro ao carregar modelos', 'error')
      }
    },
    
    async copiarUltimoProntuario() {
      const os = this.paciente?.idOrdemServico
      // Verificar se há paciente e ordem de serviço
      if (!this.paciente || !os) {
        this.showSnackbar('Paciente ou ordem de serviço não encontrada', 'error')
        return
      }

      console.log('Função copiarUltimoProntuario chamada')
      console.log('Paciente:', this.paciente)
      console.log('ID Ordem Serviço:', os)

      this.loadProntuario = true
      try {
        const { status, data } = await medicoService.medico.retornarUltimoProntuarioOS(os)
        console.log('Resposta do serviço:', { status, data })
        
        if (status === 200 && data.prontuario) {
          this.content = data.prontuario
          this.showSnackbar('Último prontuário copiado com sucesso', 'success')
          
          // Emitir evento para notificar o componente pai
          this.$emit('conteudo', this.content)
        } else {
          const fallback = this.listaProntuarios?.find(
            (item) => item?.tipo === 'Prontuário' && item?.conteudo
          )
          const fallbackConteudo = fallback?.conteudo || ''
          if (fallbackConteudo) {
            this.content = fallbackConteudo
            this.showSnackbar('Prontuário copiado a partir de Meus atendimentos', 'info')
            this.$emit('conteudo', this.content)
          } else {
            this.showSnackbar('Nenhum prontuário anterior encontrado', 'warning')
          }
        }
      } catch (error) {
        console.error('Erro ao copiar último prontuário:', error)
        this.showSnackbar('Erro ao copiar último prontuário', 'error')
      } finally {
        this.loadProntuario = false
      }
    },
    
    async cadastrarModeloProntuarioAtendimento() {
      if (!this.tituloModelo.trim()) {
        this.showSnackbar('Informe um título para o modelo', 'warning')
        return
      }

      this.loadModelos = true
      try {
        const modelo = {
          idUsuario: this.user.id,
          nome: this.tituloModelo,
          descricao: this.dialogContent,
          _id: this.dialogEditarModelo ? (this.modeloAtivo?._id || this.modelo?._id || null) : null
        }
        
        const response = await medicoService.medico.cadastrarModeloProntuarioAtendimento(modelo)
        if (response.status === 200) {
          this.showSnackbar('Modelo salvo com sucesso', 'success')
          this.dialogNovoModelo = false
          this.tituloModelo = ''
          this.dialogContent = ''
          this.buscarModeloProntuarioAtendimento()
        }
      } catch (error) {
        console.error('Erro ao salvar modelo:', error)
        this.showSnackbar('Erro ao salvar modelo', 'error')
      } finally {
        this.loadModelos = false
      }
    },
    
    salvarEdicaoModelo() {
      this.cadastrarModeloProntuarioAtendimento()
      this.dialogEditarModelo = false
    },
    
    async salvarProntuario() {
      if (!this.content.trim()) {
        this.showSnackbar('O prontuário não pode estar vazio', 'warning')
        return
      }

      const service = this.prontuarioIdLocal ? 'editarProntuario' : 'editarLaudo'
      const payload = {
        idProntuario: this.prontuarioIdLocal,
        idLaudo: this.idLaudo,
        descricao: this.content
      }

      this.loadIa = true
      try {
        const { status } = await medicoService.medico[service](payload)
        if (status === 200) {
          this.showSnackbar('Prontuário salvo com sucesso', 'success')
          this.hasUnsavedChanges = false
          this.loadSocket = false
          this.$emit('prontuarioSalvo')
        }
      } catch (error) {
        console.error('Erro ao salvar prontuário:', error)
        this.showSnackbar('Erro ao salvar prontuário', 'error')
      } finally {
        this.loadIa = false
      }
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        visible: true,
        message,
        color
      }
    },
    
    debounce(func, wait) {
      return (...args) => {
        clearTimeout(this.debounceTimeout)
        this.debounceTimeout = setTimeout(() => func.apply(this, args), wait)
      }
    },
    
    inicializarSocket() {
      this.socket = socketServiceInstance
      
      if (!this.socket.isConnected) {
        this.socket.connect(base || 'http://localhost:3000')
      }

      this.socket.on('respostaCadastroTexto', (response) => {
        this.loadSocket = true
        if (response.status === 'success') {
          this.prontuarioIdLocal = response.message?.id || this.prontuarioIdLocal
          this.prontuarioCarregado = true
          this.hasUnsavedChanges = false
          this.$emit('prontuarioSalvo', response.message.id)
          this.loadSocket = false
          this.pendingAutoSaveResolvers.splice(0).forEach(resolve => resolve(true))
          this.showSnackbar('Alterações salvas automaticamente', 'success')
        }
      })

      this.socket.on('connect_error', (error) => {
        console.error('Erro de conexão com o Socket.IO:', error)
        this.showSnackbar('Erro de conexão com o servidor', 'error')
      })

      this.socket.on('disconnect', () => {
        console.warn('Desconectado do Socket.IO')
        this.showSnackbar('Conexão perdida com o servidor', 'warning')
      })
    },
    
    // Método para definir conteúdo do editor principal
    setEditorContent(content) {
      this.content = content
    }
  }
}
</script>

<style scoped>
.premium-prontuario-editor {
  height: 100%;
}

.premium-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Toolbar */
.premium-toolbar {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.toolbar-row {
  min-height: 48px;
}

.action-btn {
  text-transform: none;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.copy-btn {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
}

.copy-btn:hover:not(:disabled) {
  border-color: rgba(var(--v-theme-on-surface), 0.4);
  transform: translateY(-1px);
}

.btn-icon {
  margin-right: 6px;
}

.btn-text {
  font-size: 0.875rem;
  font-weight: 500;
}

.models-section {
  gap: 8px;
}

.model-autocomplete {
  min-width: 200px;
}

.model-autocomplete :deep(.v-field) {
  border-radius: 6px;
}

.model-action-btn {
  border-radius: 6px;
  transition: all 0.2s ease;
}

.editor-footer {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.model-action-btn:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  transform: scale(1.05);
}

/* Editor Container */
.editor-container {
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
  min-height: 420px;
}

.editor-wrapper {
  height: 100%;
  border-radius: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.editor-status {
  padding: 6px 12px;
  border-radius: 6px;
}

.status-text {
  font-weight: 500;
}

/* Diálogos */
.premium-dialog-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.premium-dialog-header {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.dialog-title {
  font-size: 1rem;
  font-weight: 500;
}

.dialog-content {
  max-height: 60vh;
  overflow-y: auto;
}

.dialog-editor-container {
  border-radius: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  overflow: hidden;
}

.editor-label {
  display: block;
  padding: 8px 0;
}

.label-text {
  font-size: 0.875rem;
  font-weight: 500;
}

.dialog-actions {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.cancel-btn {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
}

.save-model-btn, .save-edit-btn {
  /* As cores são definidas no componente, mantendo a estilização */
}

.save-model-btn:hover, .save-edit-btn:hover {
  transform: translateY(-1px);
}

/* Hint Card */
.premium-hint-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.hint-header {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.hint-title {
  font-size: 0.875rem;
  font-weight: 500;
}

.hint-content {
  padding: 16px;
}

.info-box {
  display: flex;
  align-items: center;
  border-left: 3px solid rgba(var(--v-theme-on-surface), 0.2);
}

.hint-actions {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 12px 16px;
}

/* Responsividade */
@media (max-width: 960px) {
  .premium-toolbar {
    padding: 8px 12px;
  }
  
  .models-section {
    flex-direction: column;
    gap: 8px;
  }
  
  .model-autocomplete {
    width: 100%;
    min-width: auto;
  }
}

@media (max-width: 600px) {
  .premium-toolbar {
    padding: 6px 8px;
  }
  
  .toolbar-row {
    flex-wrap: wrap;
  }
  
  .copy-btn {
    margin-bottom: 8px;
  }
  
  .dialog-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .dialog-actions .v-btn {
    width: 100%;
  }
}

/* Animações */
.v-card {
  transition: all 0.3s ease;
}

.v-btn {
  transition: all 0.2s ease;
}

/* Scrollbar personalizado */
.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #bdbdbd;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #9e9e9e;
}
</style>
